import { compare, Operation } from 'fast-json-patch';
import { Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { Action } from '@ngrx/store';
import { CategoryType, ErrorModel } from '@rocksoft/shared';
import { catchErrorWithToast, IToastie } from '@rocksoft/shared/toast';
import { ApplicationConfiguration } from './category-applications/category-applications.state';
import { VppApplicationConfiguration } from './category-vpp-applications/category-vpp-applications.state';

type KeyedApplicationCollection<T> = Record<string, Record<string, T>>;

function compareKeyedApplications<T>(
  previousApplications: T[],
  updatedApplications: T[],
  getApplicationKey: (application: T) => string,
  getVersionKey: (application: T) => string,
  serialize: (application: T) => object
): Operation[] {
  return compare(
    toKeyedApplicationCollection(previousApplications, getApplicationKey, getVersionKey, serialize),
    toKeyedApplicationCollection(updatedApplications, getApplicationKey, getVersionKey, serialize)
  );
}

function toKeyedApplicationCollection<T>(
  applications: T[],
  getApplicationKey: (application: T) => string,
  getVersionKey: (application: T) => string,
  serialize: (application: T) => object
): KeyedApplicationCollection<object> {
  const result: KeyedApplicationCollection<object> = Object.create(null);

  applications.forEach((application) => {
    const applicationKey = getApplicationKey(application);
    const versionKey = getVersionKey(application);

    if (result[applicationKey]?.[versionKey]) {
      throw new Error(`Duplicate application configuration for ${applicationKey}/${versionKey}`);
    }

    result[applicationKey] ??= Object.create(null);
    result[applicationKey][versionKey] = serialize(application);
  });

  return result;
}

export function compareApplications(action: {
  id: string;
  categoryType: CategoryType;
  previousApplicationsLinkedToCategory: ApplicationConfiguration[];
  updatedApplicationsLinkedToCategory: ApplicationConfiguration[];
}): Operation[] {
  return compareKeyedApplications(
    action.previousApplicationsLinkedToCategory,
    action.updatedApplicationsLinkedToCategory,
    (application) => application.bundleIdentifier,
    (application) => application.version,
    (application) => ({
      bundleIdentifier: application.bundleIdentifier,
      version: application.version,
      applyFrom: application.applyFrom instanceof Date ? application.applyFrom.toISOString() : application.applyFrom,
      applyTill: application.applyTill instanceof Date ? application.applyTill.toISOString() : application.applyTill,
    })
  );
}

export function compareVppApplications(action: {
  categoryId: string;
  categoryType: CategoryType;
  previousApplicationsLinkedToCategory: VppApplicationConfiguration[];
  updatedApplicationsLinkedToCategory: VppApplicationConfiguration[];
}): Operation[] {
  return compareKeyedApplications(
    action.previousApplicationsLinkedToCategory,
    action.updatedApplicationsLinkedToCategory,
    (application) => application.adamIdStr,
    (application) => application.version ?? '',
    (application) => ({
      adamIdStr: application.adamIdStr,
      applyFrom: application.applyFrom instanceof Date ? application.applyFrom.toISOString() : application.applyFrom,
      version: application.version,
    })
  );
}

export function safeMap<T, A, E extends Action>(
  result: Observable<T> | (() => Observable<T>),
  createAction: (t: T) => A | A[],
  createError: (error: ErrorModel) => E,
  toastMessage?: string
): Observable<A | (E & { toast: IToastie })> {
  let sourceObservable: Observable<T>;
  if (typeof result == 'function') {
    sourceObservable = of(true).pipe(mergeMap(result));
  } else {
    sourceObservable = result;
  }

  return sourceObservable.pipe(
    mergeMap((x) => {
      const action = createAction(x);
      return Array.isArray(action) ? action : [action];
    }),
    catchErrorWithToast((error) => createError(error as ErrorModel), toastMessage)
  );
}
